import { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import LoadingComponent from "../../app/components/LoadingComponent";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { FormProvider, useForm } from "react-hook-form";
import { DataGrid, GridSelectionModel } from "@mui/x-data-grid";
import Papa from "papaparse";
import { getPersonalDetailsByImcnumbersAsync } from "../../app/slices/personSlice";
import { batchUploadAsync, getBatchRecordAsync, getBatchUploadContactsAsync } from "../../app/slices/batchUploadSlice";
import { BatchUploadTable } from "../../app/models/batchUpload/batchUploadTable";
import { BatchUploadCsvFileModel } from "../../app/models/batchUpload/batchUploadCsvFileModel";
import { ErrorMessageModel } from "./errorMessageModel";
import { getPcsEnrolmentsByMultiplePersonIdsAsync, getRegistrationDetailsByMultiplePersonIdsAsync } from "../../app/slices/pcsSlice";
import { BatchObject } from "./batchObject";
import { API_REQUEST_FULLFILLED } from "../../app/utils/constant";
import { formatDate_YYYY_MM_DD, GetCurrentYearAsync } from "../../app/utils/util";

export default function BatchUploadForm() {
    const dispatch = useAppDispatch();

    const { personalDetailsList, personalDetailsListStatus } = useAppSelector(state => state.person);
    const { batchUpload, batchUploadContacts, batchUploadStatus, getBatchUploadStatus, getBatchUploadContactsStatus } = useAppSelector(state => state.batchUpload);
    const { registrationDetailsList, pcsEnrolmentsByMultiplePerson, registerStateStatus, pcsEnrolmentsStatus, getPcsEnrolmentsByMultiplePersonIdsStatus, registrationDetailsListStatus } = useAppSelector(state => state.pcs);

    const methods = useForm({
        mode: 'all'
    });

    methods.watch();
    const { handleSubmit } = methods;

    const [columns, setColumns] = useState([]);
    let [rows, setRows] = useState<BatchUploadGridRow[]>([]);

    type BatchUploadGridRow = BatchUploadCsvFileModel & { id: number };
    const [csvData, setCsvData] = useState<BatchUploadGridRow[]>([]);

    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

    const [imcNumbers, setImcNumbers] = useState<string[]>();
    const [submitMessage, setSubmitMessage] = useState<string>('');
    const [batchRow, setBatchRow] = useState<BatchObject[]>([]);
    const [searchText, setSearchText] = useState("");

    const filteredRows = useMemo(() => {
        if (!searchText) return rows;

        const lowerSearch = searchText.toLowerCase();

        return rows.filter((row) =>
            Object.values(row).some((value) =>
                String(value ?? "")
                    .toLowerCase()
                    .includes(lowerSearch)
            )
        );
    }, [rows, searchText]);

    const saveBatchFileClick = () => {
        const batch: BatchUploadTable = {
            id: new URLSearchParams(window.location.search).get("id") ?? '',
            rows: rows.filter((row: any) => selectionModel.includes(row.id))
        };

        dispatch(batchUploadAsync(batch)).then((response) => {
            if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                setSubmitMessage(selectionModel.length + ' record(s) added as Batch Upload Contact(s) successfully. To continue, please refresh the page.');
            }
        });
    };

    const _handleSubmit = (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: (result: any) => {
                const data = result.data.filter((x: any, index: number) => {
                    return index !== 0;
                });

                if (!data || data.length === 0) return;
                setCsvData(data);

                const cols = [
                    { field: 'ImcNumber', headerName: 'IMC', width: 150, type: 'number', align: 'left', headerAlign: 'left' },
                    { field: 'PersonName', headerName: 'Person', width: 250 },
                    { field: 'Credits', headerName: 'Credits', width: 100, type: 'number', align: 'center', headerAlign: 'center' },
                    { field: 'AdditionalInfo', headerName: 'Info', flex: 1 }
                ];

                let imcNumbers: string[] = [];

                data.map((row: any) => imcNumbers.push(row[0]));

                setImcNumbers(imcNumbers);

                imcNumbers && dispatch(getPersonalDetailsByImcnumbersAsync(imcNumbers));

                const batchId = new URLSearchParams(window.location.search).get("id");
                batchId && dispatch(getBatchUploadContactsAsync(batchId));

                setColumns(cols as any);
            },
            error: (error: any) => {
                console.error("CSV Parse Error:", error);
            },
        });
    };

    useEffect(() => {
        if (imcNumbers && imcNumbers?.length > 0 && personalDetailsList && personalDetailsList.length > 0) {
            let personalDetailsObj: BatchObject[] = [];
            let personIdsTocheckPcsProfile: string[] = [];

            for (let index = 0; index < imcNumbers.length; index++) {
                const contacts = personalDetailsList?.filter(x => x.imcNumber === imcNumbers[index]);

                let errorMessageModel: ErrorMessageModel = { errorMessage: '', canBeSelected: true };
                let personName = contacts !== null && contacts.length > 0 && contacts[0].fullName ? contacts[0].fullName : '';

                if (contacts && contacts.length === 0) {
                    errorMessageModel.errorMessage += "Contact not in CRM!";
                    errorMessageModel.canBeSelected = false;
                }
                //Reason for contacts.length === imcNumbers.filter(x => x !== imcNumbers[index]).length, if we request 2 same imc numbers, API will return 2 same personal details
                else if (contacts && contacts.length > 1 && contacts.length !== imcNumbers.filter(x => x === imcNumbers[index]).length) {
                    personName = '';
                    errorMessageModel.errorMessage += "More than 1 Contact found in CRM with that IMC Number!";
                    errorMessageModel.canBeSelected = false;
                }
                else if (batchUploadContacts) {
                    contacts.map(x => x.id && personIdsTocheckPcsProfile.push(x.id));
                }

                personalDetailsObj.push({
                    id: personalDetailsObj.length + 1,
                    ImcNumber: imcNumbers[index],
                    PersonName: personName,
                    Credits: csvData.find((x: any) => x[0] === imcNumbers[index])?.Credits ?? 0,
                    AdditionalInfo: errorMessageModel.errorMessage ?? '',
                    CanBeSelected: errorMessageModel.canBeSelected === undefined ? false : errorMessageModel.canBeSelected
                });
            }

            dispatch(getRegistrationDetailsByMultiplePersonIdsAsync(personIdsTocheckPcsProfile));
            setBatchRow(personalDetailsObj);

            const rowsWithId = csvData.map((row: any, index: number) => ({
                id: index + 1,
                ImcNumber: row[0],
                PersonName: personalDetailsObj.find(x => x.ImcNumber === row[0])?.PersonName,
                Credits: row[1],
                AdditionalInfo: personalDetailsObj.find(x => x.ImcNumber === row[0])?.AdditionalInfo,
                CanBeSelected: personalDetailsObj.find(x => x.ImcNumber === row[0])?.CanBeSelected,
                ...row,
            }));
            setRows(rowsWithId);
        }
    }, [personalDetailsList, imcNumbers, csvData, batchUploadContacts, dispatch]);

    useEffect(() => {
        if (imcNumbers && imcNumbers?.length > 0 && registrationDetailsList && registrationDetailsList.length > 0
            && personalDetailsList && personalDetailsList.length > 0) {
            let personIdsTocheckPcsEnrolment: string[] = [];
            let personalDetailsObj: BatchObject[] = batchRow;

            for (let index = 0; index < personalDetailsList.length; index++) {
                const contact = personalDetailsList[index];
                const pcsProfile = registrationDetailsList.find(x => x.personId === contact.id);

                if ((pcsProfile === undefined || pcsProfile === null) &&
                    personalDetailsObj.find(o => o.ImcNumber === personalDetailsList[index].imcNumber)!.AdditionalInfo === '') {
                    personalDetailsObj.find(o => o.ImcNumber === personalDetailsList[index].imcNumber)!.AdditionalInfo +=
                        "PCS Profile not in CRM!";
                } else if (pcsProfile !== null && pcsProfile !== undefined) {
                    contact.id && personIdsTocheckPcsEnrolment.push(contact.id);
                }
            }
            dispatch(getPcsEnrolmentsByMultiplePersonIdsAsync(personIdsTocheckPcsEnrolment));

            const batchId = new URLSearchParams(window.location.search).get("id") ?? '';
            dispatch(getBatchRecordAsync(batchId));
            setBatchRow(personalDetailsObj);

            const rowsWithId = csvData.map((row: any, index: number) => ({
                id: index + 1,
                ImcNumber: row[0],
                PersonName: personalDetailsObj.find(x => x.ImcNumber === row[0])?.PersonName,
                Credits: row[1],
                AdditionalInfo: personalDetailsObj.find(x => x.ImcNumber === row[0])?.AdditionalInfo,
                CanBeSelected: personalDetailsObj.find(x => x.ImcNumber === row[0])?.CanBeSelected,
                ...row,
            }));
            setRows(rowsWithId);
        }
    }, [personalDetailsList, imcNumbers, csvData, batchUploadContacts, registrationDetailsList, batchRow, dispatch]);

    useEffect(() => {
        if (imcNumbers && imcNumbers?.length > 0 && pcsEnrolmentsByMultiplePerson && pcsEnrolmentsByMultiplePerson.length > 0
            && personalDetailsList && personalDetailsList.length > 0) {
            let personalDetailsObj: BatchObject[] = batchRow;

            for (let index = 0; index < personalDetailsList.length; index++) {
                const contact = personalDetailsList[index];
                const pcsEnrolment = pcsEnrolmentsByMultiplePerson.find(x => x.personId === contact.id);

                if ((pcsEnrolment === undefined || pcsEnrolment === null) &&
                    personalDetailsObj.find(o => o.ImcNumber === personalDetailsList[index].imcNumber)!.AdditionalInfo === '') {
                    personalDetailsObj.find(o => o.ImcNumber === personalDetailsList[index].imcNumber)!.AdditionalInfo +=
                        "PCS Enrolment not in CRM!";
                } else {
                    const currentYear = String(GetCurrentYearAsync(new Date(batchUpload?.dateOfActivity as string)));
                    const enrolment = pcsEnrolmentsByMultiplePerson.filter(x => x.schemeYearId_Description === currentYear && x.personId === contact.id);
                    if ((enrolment === null || enrolment.length === 0) && personalDetailsObj.find(o => o.ImcNumber === contact.imcNumber)!.AdditionalInfo === '') {
                        personalDetailsObj.find(o => o.ImcNumber === contact.imcNumber)!.AdditionalInfo +=
                            `No PCS Enrolment for the year ${currentYear} in CRM!`;
                    }
                }
            }
            setBatchRow(personalDetailsObj);

            const rowsWithId = csvData.map((row: any, index: number) => ({
                id: index + 1,
                ImcNumber: row[0],
                PersonName: personalDetailsObj.find(x => x.ImcNumber === row[0])?.PersonName,
                Credits: row[1],
                AdditionalInfo: personalDetailsObj.find(x => x.ImcNumber === row[0])?.AdditionalInfo,
                CanBeSelected: personalDetailsObj.find(x => x.ImcNumber === row[0])?.CanBeSelected,
                ...row,
            }));
            setRows(rowsWithId);
        }
    }, [personalDetailsList, imcNumbers, csvData, batchUploadContacts, pcsEnrolmentsByMultiplePerson, batchRow, batchUpload?.dateOfActivity]);

    const exportSelectedToCsv = () => {
        if (rows.length === 0) return;

        const headers = ["IMC", "Person", "Credits", "Info"];
        const lines = [
            headers.join(","),
            ...rows.map(r =>
                [
                    r.ImcNumber,
                    `"${(r.PersonName ?? "").replace(/"/g, '""')}"`,
                    r.Credits,
                    `"${(r.AdditionalInfo ?? "").replace(/"/g, '""')}"`
                ].join(",")
            ),
        ];

        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const batchId = new URLSearchParams(window.location.search).get("id");
        const a = document.createElement("a");
        a.href = url;
        a.download = `Export_BatchUpload_${batchId}_${formatDate_YYYY_MM_DD(new Date())}`;
        a.click();

        URL.revokeObjectURL(url);
    };

    if (personalDetailsListStatus === ApiRequestStatus.Pending || pcsEnrolmentsStatus === ApiRequestStatus.Pending
        || batchUploadStatus === ApiRequestStatus.Pending || registerStateStatus === ApiRequestStatus.Pending
        || getBatchUploadStatus === ApiRequestStatus.Pending || getBatchUploadContactsStatus === ApiRequestStatus.Pending
        || getPcsEnrolmentsByMultiplePersonIdsStatus === ApiRequestStatus.Pending
        || registrationDetailsListStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message='Processing details...' />
    }

    if (batchUploadStatus === ApiRequestStatus.Fulfilled) {
        return <>
            <Typography sx={{ color: 'green', fontWeight: 'bold', mt: '10px' }}>{submitMessage}</Typography>
            <br></br>
            <Button variant="contained" onClick={() => window.location.reload()}>Refresh</Button>
        </>
    }

    return <FormProvider {...methods}>
        <form onSubmit={handleSubmit(_handleSubmit)} style={{ left: 0, position: 'absolute', width: '100%' }}>
            <Grid container rowSpacing={4}>

                <Grid item xs={12}></Grid>

                {filteredRows.length === 0 && <>
                    <Grid item xs={2}>
                        Please upload a batch file
                    </Grid>

                    <Grid item xs={1}></Grid>

                    <Grid item xs={9} sx={{ alignSelf: 'center' }}>
                        <input type="file" accept=".csv" onChange={_handleSubmit} />
                    </Grid>
                </>}

                <Grid item xs={3} sx={{ textAlign: "left" }}>
                    {filteredRows.length > 0 && <Button variant="contained" onClick={() => window.location.reload()}>
                        Upload new file
                    </Button>}
                </Grid>

                <Grid item xs={3} sx={{ textAlign: "left" }}>
                    {filteredRows.length > 0 && <Button variant="contained" onClick={exportSelectedToCsv}>
                        Export to CSV
                    </Button>}
                </Grid>

                <Grid item xs={6} sx={{ textAlign: "right" }}>
                    {filteredRows.length > 0 && <TextField label="Search" variant="outlined" size="small" fullWidth
                        sx={{ mb: 2, maxWidth: 400 }} value={searchText} onChange={(e) => setSearchText(e.target.value)} />}
                </Grid>
            </Grid>

            {rows.length > 0 && <Box sx={{ mt: 2 }}>
                <DataGrid autoHeight rows={filteredRows} columns={columns} checkboxSelection selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                    isRowSelectable={(param) => param.row.CanBeSelected === true}
                    rowsPerPageOptions={[]} />
            </Box>}

            <Box sx={{ mt: 2 }}>
                {filteredRows.length > 0 && selectionModel.length > 0 && <Button variant="contained" onClick={saveBatchFileClick}>
                    Submit Selected
                </Button>}
            </Box>
        </form>
    </FormProvider>;
}
